import { useApolloClient } from '@apollo/client'
import { EmployeeCollectionListIdsDocument } from 'generated/resources/graphql'
import { EmployeeCollectionListIdsEmployeeFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { toDateString } from 'modules/employees/lib/utils'

type DateValue = Date | string

export interface BulkActionFilters {
  team?: string[]
  workplace?: string[]
  jobRole?: string[]
  jobTitle?: string[]
  legalEntity?: string[]
  employeeGroup?: string[]
  timeoffManager?: string[]
  timeoffPolicy?: string[]
  hiringDate?: DateValue | { from: DateValue; to?: DateValue } | { from?: DateValue; to: DateValue }
  contractEndDate?:
    | DateValue
    | { from: DateValue; to?: DateValue }
    | { from?: DateValue; to: DateValue }
  contractStatus?: string[]
  accessStatus?: string[]
  promotionStatus?: string[]
  search?: string
  directReports?: boolean[]
  reportingTo?: string[]
}

interface UseEmployeesBulkActionsParams {
  hasJobCatalogTreeFeature: boolean
  canSeeFormerEmployees: boolean
}

export const useEmployeesBulkActions = ({
  hasJobCatalogTreeFeature,
  canSeeFormerEmployees,
}: UseEmployeesBulkActionsParams) => {
  const client = useApolloClient()
  const { company } = useCurrentSession()

  const getSelectedEmployees = async <T extends EmployeeCollectionListIdsEmployeeFragment>({
    allSelected,
    itemsStatus,
    filters,
  }: {
    allSelected: boolean | 'indeterminate'
    itemsStatus: readonly {
      item: T
      checked: boolean
    }[]
    filters: BulkActionFilters
  }): Promise<T[]> => {
    const manualSelectedEmployees = itemsStatus
      .filter((item) => item.checked)
      .map((item) => item.item)

    if (allSelected === false) {
      return manualSelectedEmployees
    }

    const excludedIds =
      allSelected === 'indeterminate'
        ? itemsStatus.filter((item) => !item.checked).map((item) => item.item.id)
        : []

    // Apply the same filter transformation as in mapVariables
    const teamIds = filters.team
    const locationIds = filters.workplace
    const jobLevelIds = !hasJobCatalogTreeFeature ? filters.jobRole : undefined
    const jobCatalogTreeNodeIds = hasJobCatalogTreeFeature ? filters.jobRole : undefined
    const jobTitles = filters.jobTitle
    const legalEntityIds = filters.legalEntity
    const employeeGroupIds = filters.employeeGroup
    const timeoffManagerIds = filters.timeoffManager
    const timeoffPolicyIds = filters.timeoffPolicy
    const hiringDateRange = filters.hiringDate
    const hiringDateFrom =
      hiringDateRange && typeof hiringDateRange === 'object' && 'from' in hiringDateRange
        ? toDateString(hiringDateRange.from)
        : undefined

    const hiringDateTo =
      hiringDateRange && typeof hiringDateRange === 'object' && 'to' in hiringDateRange
        ? toDateString(hiringDateRange.to)
        : undefined

    const contractEndDateRange = filters.contractEndDate
    const contractEndDateFrom =
      contractEndDateRange &&
      typeof contractEndDateRange === 'object' &&
      'from' in contractEndDateRange
        ? toDateString(contractEndDateRange.from)
        : undefined

    const contractEndDateTo =
      contractEndDateRange &&
      typeof contractEndDateRange === 'object' &&
      'to' in contractEndDateRange
        ? toDateString(contractEndDateRange.to)
        : undefined

    const contractStatus = filters.contractStatus
    const accessStatus = filters.accessStatus
    const promotionStatus = filters.promotionStatus

    // By default, exclude terminated employees unless explicitly filtered
    const accessStatusWithDefault = accessStatus?.length
      ? accessStatus
      : canSeeFormerEmployees
        ? ['not_invited', 'accepted', 'active', 'invited']
        : undefined

    const { data } = await client.query({
      query: EmployeeCollectionListIdsDocument,
      variables: {
        companyId: company.id,
        onlyMyReportees: filters.directReports?.includes(true),
        reportingTo: filters.reportingTo,
        referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
        sort: undefined,
        search: filters.search,
        teamIds: teamIds?.length ? teamIds : undefined,
        locationIds: locationIds?.length ? locationIds : undefined,
        referenceContractJobTitles: jobTitles?.length ? jobTitles : undefined,
        referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
          ? jobCatalogTreeNodeIds
          : undefined,
        legalEntityIds: legalEntityIds?.length ? legalEntityIds : undefined,
        employeeGroupIds: employeeGroupIds?.length ? employeeGroupIds : undefined,
        timeoffManagerIds: timeoffManagerIds?.length ? timeoffManagerIds : undefined,
        timeoffPolicyIds: timeoffPolicyIds?.length ? timeoffPolicyIds : undefined,
        referenceContractStartsOnGteq: hiringDateFrom,
        referenceContractStartsOnLteq: hiringDateTo,
        referenceContractEndsOnGteq: contractEndDateFrom,
        referenceContractEndsOnLteq: contractEndDateTo,
        contractStatus: contractStatus?.length ? contractStatus : undefined,
        accessStatus: accessStatusWithDefault,
        promotionStatus: promotionStatus?.length ? promotionStatus : undefined,
      },
    })

    return data.employees.employees
      .filter((employee) => !excludedIds.includes(employee.id))
      .filter(Boolean)
  }

  return {
    getSelectedEmployees,
  }
}
